import { falHeart } from 'shared/@generated/icons/falHeart';
import { falSpinnerThird } from 'shared/@generated/icons/falSpinnerThird';
import { fasHeart } from 'shared/@generated/icons/fasHeart';
import { TouchableOpacity, View } from 'react-native';
import { Icon } from 'shared/components/icon';
import { Like, Props } from 'shared/@types';
import useLikesStore from 'shared/state/likes';
import useApi from 'shared/library/use-api';
import { useShowLoginModalIfGuest } from 'shared/state/login';
import { notify } from 'shared/library/notify';
import { apiRoute } from 'shared/library/api-route';
import { Button } from 'shared/components/button';
import { useTranslation } from 'react-i18next';
import { Text } from 'shared/components/text';
import { useEffect, useRef } from 'react';
import { useUserStore } from 'shared/state/user';
import { ApiRoute } from 'shared/library/api-route';
import { RequestClient } from 'shared/library/api-client';

type LikeStatus = {
  model_id: number;
  model_type: string;
  liked: boolean;
  like: Like | null;
};

const likesCheckRoute: ApiRoute = { url: '/me/likes/check', method: 'post' };
const pendingLikeStatusChecks = new Map<string, { model_id: number; model_type: string }>();
let likeStatusCheckTimer: ReturnType<typeof setTimeout> | null = null;

function likeStatusKey(modelId: number, modelType: string) {
  return `${modelType}:${modelId}`;
}

function scheduleLikeStatusCheck(
  modelId: number,
  modelType: string,
  request: RequestClient,
  setStatuses: (statuses: LikeStatus[]) => void,
) {
  pendingLikeStatusChecks.set(likeStatusKey(modelId, modelType), {
    model_id: modelId,
    model_type: modelType,
  });

  if (likeStatusCheckTimer) {
    return;
  }

  likeStatusCheckTimer = setTimeout(() => {
    void flushLikeStatusChecks(request, setStatuses);
  }, 25);
}

async function flushLikeStatusChecks(
  request: RequestClient,
  setStatuses: (statuses: LikeStatus[]) => void,
) {
  likeStatusCheckTimer = null;

  const models = Array.from(pendingLikeStatusChecks.values()).slice(0, 100);
  models.forEach((model) => {
    pendingLikeStatusChecks.delete(likeStatusKey(model.model_id, model.model_type));
  });

  if (models.length === 0) {
    return;
  }

  try {
    const response = await request<{ likes: LikeStatus[] }>(likesCheckRoute, { models });
    setStatuses(response.likes);
  } catch {
    // The button can still fall back to optimistic state and toggle errors.
  }

  if (pendingLikeStatusChecks.size > 0) {
    likeStatusCheckTimer = setTimeout(() => {
      void flushLikeStatusChecks(request, setStatuses);
    }, 25);
  }
}

export default function LikeButton({
  modelId,
  modelType,
  withText = false,
  testID,
  className,
  textClassName,
  dense = true,
  center = false,
  textVariant = 'follow',
}: Props<{
  modelId: number;
  modelType: string;
  withText?: boolean;
  testID?: string;
  className?: string;
  textClassName?: string;
  dense?: boolean;
  center?: boolean;
  textVariant?: 'follow' | 'like';
}>) {
  const { t } = useTranslation('components');
  const showLoginModalIfGuest = useShowLoginModalIfGuest();
  const hasLiked = useLikesStore((state) => state.hasLiked(modelId, modelType));
  const setStatuses = useLikesStore((state) => state.setStatuses);
  const add = useLikesStore((state) => state.add);
  const remove = useLikesStore((state) => state.remove);
  const { request, loading } = useApi();
  const { request: statusRequest } = useApi({ notifyOnServerErrors: false });
  const statusRequestRef = useRef(statusRequest);
  const isLoggedIn = useUserStore((state) => state.user !== null);

  useEffect(() => {
    statusRequestRef.current = statusRequest;
  }, [statusRequest]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    scheduleLikeStatusCheck(modelId, modelType, statusRequestRef.current, setStatuses);
  }, [isLoggedIn, modelId, modelType, setStatuses]);

  function toggleLike() {
    if (showLoginModalIfGuest({ source: 'like-button' })) {
      return;
    }

    request<{ like: Like | null }>(apiRoute('likes.toggle'), {
      model_id: modelId,
      model_type: modelType,
      action: hasLiked ? 'unlike' : 'like',
    })
      .then(({ like }) => {
        if (like) {
          add(like);
        } else {
          remove(modelId, modelType);
        }
      })
      .catch((err) =>
        notify.error(
          err instanceof Error ? err.message : String(err),
          'like-button.tsx - toggleLike',
        ),
      );
  }

  const icon = loading ? falSpinnerThird : hasLiked ? fasHeart : falHeart;
  const withTextIconSizeClassName = dense ? 'size-4' : 'size-5';
  const iconClassName = `${withText ? withTextIconSizeClassName : ''} ${
    loading
      ? 'animate-spin text-stone-900'
      : hasLiked
        ? 'text-red-600'
        : withText
          ? 'text-brand-brown-dark'
          : 'text-stone-900'
  }`;
  const label = hasLiked
    ? t(`like_button.${textVariant === 'like' ? 'liked' : 'following'}`)
    : t(`like_button.${textVariant}`);

  return withText ? (
    <Button
      onPress={toggleLike}
      loading={loading}
      color={'white'}
      dense={dense}
      center={center}
      className={className}
      textClassName={textClassName}
      testID={testID}
    >
      <View className={`mr-1.5 items-center justify-center md:mr-3 ${withTextIconSizeClassName}`}>
        <Icon key={loading ? 'loading' : 'heart'} icon={icon} className={iconClassName} />
      </View>

      <Text
        className={`text-brand-brown-dark font-semibold ${dense ? 'text-sm md:text-base' : 'md:text-lg'} ${textClassName ?? ''}`}
      >
        {label}
      </Text>
    </Button>
  ) : (
    <TouchableOpacity
      className={`shadow-brand-brown-dark/15 rounded-full bg-white p-3 shadow ${className ?? ''}`}
      activeOpacity={0.82}
      delayPressIn={0}
      delayPressOut={0}
      accessibilityLabel={label}
      accessibilityRole={'button'}
      onPress={toggleLike}
      disabled={loading}
      testID={testID}
    >
      <Icon key={loading ? 'loading' : 'heart'} icon={icon} className={iconClassName} />
    </TouchableOpacity>
  );
}
